/**
 * useSidebarAddProjectFlow — state and callbacks for the "Add Project" UX in
 * the sidebar.
 *
 * Owns:
 *  - `addingProject` (panel open/closed) + `newCwd` (desktop path value)
 *  - `isPickingFolder` + `isAddingProject` (request-in-flight flags)
 *  - `addProjectError` + `addProjectInputRef`
 *  - the `handleStartAddProject` / `handlePickFolder` / `handleAddProject`
 *    callbacks, plus the shared `addProjectFromInput` helper that dispatches
 *    the `project.create` command against the active environment.
 *
 * Extracted from `useSidebarOrchestration` to keep the orchestration hook
 * composition-focused — behaviour is a pure move, with the same dependency
 * wiring as before.
 */

import { type RefObject, useCallback, useRef, useState } from "react";
import { type EnvironmentId, type ProjectId, type ThreadEnvMode } from "@cmmd-forge/contracts";
import type { ThreadRuntimeOption } from "@cmmd-forge/contracts/settings";
import { readLocalApi } from "../../localApi";
import { readEnvironmentApi } from "../../environmentApi";
import { toastManager } from "../ui/toast";
import type { Project } from "../../types";
import type { useNewThreadHandler } from "../../hooks/useHandleNewThread";
import { useCommandPaletteStore } from "../../commandPaletteStore";
import { registerProjectAndOpenThread, resolveImportProviders } from "../../lib/addProject";
import { useServerProviders } from "../../rpc/serverState";
import { resolveProjectImportRuntimeTarget } from "../../lib/threadRuntimeTarget";
import { useOptionalSidebar } from "../ui/sidebar";
import { readRuntimeSurfaceState } from "../../environments/runtimeSurface";

// ---------------------------------------------------------------------------
// Input / return types
// ---------------------------------------------------------------------------

export interface SidebarAddProjectFlowInput {
  readonly activeEnvironmentId: EnvironmentId | null;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly projects: readonly Project[];
  readonly defaultThreadRuntime: ThreadRuntimeOption;
  readonly defaultThreadEnvMode: ThreadEnvMode;
  readonly handleNewThread: ReturnType<typeof useNewThreadHandler>["handleNewThread"];
  readonly focusMostRecentThreadForProject: (projectRef: {
    environmentId: EnvironmentId;
    projectId: ProjectId;
  }) => void;
}

export interface SidebarAddProjectFlowResult {
  readonly addingProject: boolean;
  readonly setAddingProject: React.Dispatch<React.SetStateAction<boolean>>;
  readonly shouldShowProjectPathEntry: boolean;
  readonly canUseGitHubRepoPicker: boolean;
  readonly newCwd: string;
  readonly setNewCwd: React.Dispatch<React.SetStateAction<string>>;
  readonly isPickingFolder: boolean;
  readonly isAddingProject: boolean;
  readonly addProjectError: string | null;
  readonly setAddProjectError: React.Dispatch<React.SetStateAction<string | null>>;
  readonly addProjectInputRef: RefObject<HTMLInputElement | null>;
  readonly canAddProject: boolean;
  readonly addProjectFromInput: (cwd: string) => Promise<void>;
  readonly handleAddProject: () => void;
  readonly handlePickFolder: () => Promise<boolean>;
  readonly handleStartAddProject: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSidebarAddProjectFlow(
  input: SidebarAddProjectFlowInput,
): SidebarAddProjectFlowResult {
  const {
    activeEnvironmentId,
    primaryEnvironmentId,
    projects,
    defaultThreadRuntime,
    defaultThreadEnvMode,
    handleNewThread,
    focusMostRecentThreadForProject,
  } = input;
  const sidebar = useOptionalSidebar();
  const serverProviders = useServerProviders();

  const [addingProject, setAddingProject] = useState(false);
  const [newCwd, setNewCwd] = useState("");
  const [isPickingFolder, setIsPickingFolder] = useState(false);
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [addProjectError, setAddProjectError] = useState<string | null>(null);
  const addProjectInputRef = useRef<HTMLInputElement | null>(null);

  const shouldShowProjectPathEntry = addingProject;
  const canUseGitHubRepoPicker = activeEnvironmentId !== null;

  const addProjectFromInput = useCallback(
    async (rawCwd: string) => {
      const cwd = rawCwd.trim();
      if (!cwd || isAddingProject) return;
      const target = resolveProjectImportRuntimeTarget({
        requestedRuntime: defaultThreadRuntime,
        activeEnvironmentId,
        primaryEnvironmentId,
        readEnvironmentApi,
      });
      if (!target) {
        toastManager.add({
          type: "error",
          title: "Project cloning is unavailable",
          description: "Forge needs an active Command environment before it can add projects.",
        });
        return;
      }

      setIsAddingProject(true);
      const finishAddingProject = () => {
        setIsAddingProject(false);
        setNewCwd("");
        setAddProjectError(null);
        setAddingProject(false);
        if (sidebar) {
          if (sidebar.isMobile) {
            sidebar.setOpenMobile(false);
          } else {
            sidebar.setOpen(false);
          }
        }
      };

      try {
        await registerProjectAndOpenThread({
          api: target.api,
          environmentId: target.environmentId,
          projects,
          cwd,
          defaultThreadEnvMode,
          handleNewThread,
          focusMostRecentThreadForProject,
          // Only when the target IS the connection those providers describe.
          // Forge has one global provider snapshot, so claiming it for a sprite
          // would pin a model that connection may not have.
          ...(() => {
            const providers = resolveImportProviders(
              target.environmentId,
              primaryEnvironmentId,
              serverProviders,
            );
            return providers === undefined ? {} : { providers };
          })(),
        });
      } catch (error) {
        const description =
          error instanceof Error ? error.message : "An error occurred while adding the project.";
        setIsAddingProject(false);
        if (!addingProject) {
          toastManager.add({ type: "error", title: "Failed to add project", description });
        } else {
          setAddProjectError(description);
        }
        return;
      }
      finishAddingProject();
    },
    [
      focusMostRecentThreadForProject,
      defaultThreadRuntime,
      handleNewThread,
      isAddingProject,
      addingProject,
      activeEnvironmentId,
      primaryEnvironmentId,
      projects,
      defaultThreadEnvMode,
      serverProviders,
      sidebar,
    ],
  );

  const handleAddProject = useCallback(() => {
    if (!newCwd.trim()) {
      setAddProjectError("Path is required");
      return;
    }
    void addProjectFromInput(newCwd);
  }, [addProjectFromInput, newCwd, setAddProjectError]);

  const canAddProject = newCwd.trim().length > 0 && !isAddingProject;

  const handlePickFolder = useCallback(async (): Promise<boolean> => {
    const api = readLocalApi();
    if (!api || isPickingFolder) return false;
    setIsPickingFolder(true);
    try {
      const pickedPath = await api.dialogs.pickFolder();
      if (!pickedPath) {
        addProjectInputRef.current?.focus();
        return false;
      }
      await addProjectFromInput(pickedPath);
      return true;
    } catch (error) {
      console.error("[handlePickFolder]", error);
      return false;
    } finally {
      setIsPickingFolder(false);
    }
  }, [addProjectFromInput, isPickingFolder]);

  const handleStartAddProject = useCallback(() => {
    setAddProjectError(null);
    setAddingProject(false);
    if (sidebar?.isMobile) {
      sidebar.setOpenMobile(false);
    }
    const surface = readRuntimeSurfaceState().surface;
    const palette = useCommandPaletteStore.getState();
    if (surface === "cloud-official") {
      palette.openAddCloudProjectSources();
    } else if (surface === "desktop-cloud") {
      palette.openAddProjectSources();
    } else {
      palette.openAddLocalProjectSources();
    }
  }, [sidebar]);

  return {
    addingProject,
    setAddingProject,
    shouldShowProjectPathEntry,
    canUseGitHubRepoPicker,
    newCwd,
    setNewCwd,
    isPickingFolder,
    isAddingProject,
    addProjectError,
    setAddProjectError,
    addProjectInputRef,
    canAddProject,
    addProjectFromInput,
    handleAddProject,
    handlePickFolder,
    handleStartAddProject,
  };
}
